import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import { projectsAPI } from '../../api/projects';
import { showSuccess, showError } from '../../utils/toast';

import { getApiBaseUrl } from '../../api/apiConfig';
import './ProjectForm.css';

const slugify = (text) => {
  return (text || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
};

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const base = getApiBaseUrl();
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  try {
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      return match ? `https://www.youtube.com/embed/${match[1]}` : null;
    }
    if (url.includes('vimeo.com')) {
      const match = url.match(/vimeo\.com\/(\d+)/);
      return match ? `https://player.vimeo.com/video/${match[1]}` : null;
    }
    return null;
  } catch {
    return null;
  }
};

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: '',
    description: '',
    client_name: '',
    vimeo_youtube_url: '',
    video_url: '',
    project_date: '',
    cover_image: '',
    cover_image_url: '',
    is_featured: false,
    is_published: true,
    is_active: true,
  });
  const [files, setFiles] = useState({ cover_image: null, video_file: null, media_files: [] });
  const [mediaUrls, setMediaUrls] = useState('');
  const coverInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const mediaInputRef = useRef(null);
  const [existingMedia, setExistingMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchProject = useCallback(async () => {
    if (!id) return;
    try {
      const project = await projectsAPI.getById(id);
      setFormData({
        title: project.title || '',
        slug: project.slug || slugify(project.title || ''),
        category: project.category || '',
        description: project.description || '',
        client_name: project.client_name || '',
        vimeo_youtube_url: project.vimeo_youtube_url || '',
        video_url: project.video_url || '',
        project_date: project.project_date ? project.project_date.split('T')[0] : '',
        cover_image: project.cover_image || '',
        cover_image_url: (project.cover_image && project.cover_image.startsWith('http')) ? project.cover_image : '',
        is_featured: project.is_featured || false,
        is_published: project.is_published !== undefined ? project.is_published : true,
        is_active: project.is_active !== undefined ? project.is_active : true,
      });
      if (project.media && project.media.length > 0) {
        setExistingMedia(project.media);
        const urls = project.media
          .filter((m) => (m.file_path || '').startsWith('http'))
          .map((m) => m.file_path)
          .join('\n');
        setMediaUrls(urls);
      }
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Failed to load project data');
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      fetchProject();
    }
  }, [isEdit, fetchProject]);

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setFormData((prev) => ({ ...prev, title: newTitle, slug: slugify(newTitle) }));
  };

  const coverPreviewSrc = () => {
    if (files.cover_image) return URL.createObjectURL(files.cover_image);
    const url = (formData.cover_image_url || '').trim();
    if (url && (url.startsWith('http') || url.startsWith('//'))) return url.startsWith('//') ? `https:${url}` : url;
    if (isEdit && formData.cover_image) return getImageUrl(formData.cover_image);
    return null;
  };

  const videoPreviewSrc = () => {
    const embed = getVideoEmbedUrl(formData.vimeo_youtube_url);
    if (embed) return { type: 'embed', url: embed };
    if (files.video_file) return { type: 'file', url: URL.createObjectURL(files.video_file) };
    const direct = (formData.video_url || '').trim();
    if (direct && direct.startsWith('http')) return { type: 'url', url: direct };
    if (isEdit && formData.video_url) return { type: 'url', url: getImageUrl(formData.video_url) || formData.video_url };
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('slug', formData.slug || slugify(formData.title));
    formDataToSend.append('category', formData.category);
    formDataToSend.append('description', formData.description || '');
    formDataToSend.append('client_name', formData.client_name || '');
    formDataToSend.append('vimeo_youtube_url', formData.vimeo_youtube_url || '');
    formDataToSend.append('video_url', formData.video_url || '');
    formDataToSend.append('project_date', formData.project_date || '');
    formDataToSend.append('cover_image_url', (formData.cover_image_url || '').trim());
    formDataToSend.append('is_featured', formData.is_featured ? 'true' : 'false');
    formDataToSend.append('is_published', formData.is_published ? 'true' : 'false');
    formDataToSend.append('is_active', formData.is_active ? 'true' : 'false');

    if (files.cover_image) {
      formDataToSend.append('cover_image', files.cover_image);
    }
    if (files.video_file) {
      formDataToSend.append('video_file', files.video_file);
    }
    files.media_files.forEach((file) => {
      formDataToSend.append('media_files', file);
    });
    formDataToSend.append('media_urls', mediaUrls.trim());

    try {
      if (isEdit) {
        await projectsAPI.update(id, formDataToSend);
      } else {
        await projectsAPI.create(formDataToSend);
      }
      showSuccess('Project saved successfully!');
      navigate('/projects');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save project. Please try again.';
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  const videoPreview = videoPreviewSrc();

  return (
    <div>
      <div className="project-form-header">
        <Link to="/projects" className="project-form-back">
          <ArrowLeft size={18} /> Back to Projects
        </Link>
        <h1>{isEdit ? 'Edit Project' : 'New Project'}</h1>
      </div>

      <form className="project-form project-form-card" onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem' }}>
          <label>Title *</label>
          <input
            type="text"
            value={formData.title}
            onChange={handleTitleChange}
            required
            className="project-form-field"
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Category *</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            required
            className="project-form-field"
          >
            <option value="">Select Category</option>
            <option value="Video">Video</option>
            <option value="Photography">Photography</option>
            <option value="Graphic Design">Graphic Design</option>
            <option value="Web Design">Web Design</option>
            <option value="3D">3D</option>
            <option value="Event">Event</option>
          </select>
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Cover Image</label>
          <div style={{ marginBottom: '0.5rem' }}>
            <label className="project-form-sublabel">Upload file:</label>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setFiles((f) => ({ ...f, cover_image: file }));
                if (file) setFormData((d) => ({ ...d, cover_image_url: '' }));
              }}
              className="project-form-field"
            />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label className="project-form-sublabel">Or paste image URL (https://...):</label>
            <input
              type="url"
              value={formData.cover_image_url}
              onChange={(e) => {
                setFormData((d) => ({ ...d, cover_image_url: e.target.value }));
                if (e.target.value) setFiles((f) => ({ ...f, cover_image: null }));
              }}
              placeholder="https://example.com/image.jpg"
              className="project-form-field"
            />
          </div>
          {coverPreviewSrc() && (
            <div style={{ marginTop: '0.5rem', position: 'relative', display: 'inline-block' }}>
              <p className="project-form-preview-caption">Preview:</p>
              <img
                src={coverPreviewSrc()}
                alt="Cover preview"
                style={{ maxWidth: '280px', maxHeight: '180px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border-subtle)' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {files.cover_image && (
                <button
                  type="button"
                  onClick={() => {
                    setFiles((f) => ({ ...f, cover_image: null }));
                    if (coverInputRef.current) coverInputRef.current.value = '';
                  }}
                  className="project-form-remove-btn"
                  title="Remove cover image"
                  aria-label="Remove cover image"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows="5"
            className="project-form-field"
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Client Name</label>
          <input
            type="text"
            value={formData.client_name}
            onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
            className="project-form-field"
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Video</label>
          <div style={{ marginBottom: '0.5rem' }}>
            <label className="project-form-sublabel">YouTube or Vimeo URL:</label>
            <input
              type="url"
              value={formData.vimeo_youtube_url}
              onChange={(e) => setFormData({ ...formData, vimeo_youtube_url: e.target.value })}
              placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
              className="project-form-field"
            />
          </div>
          <div style={{ marginBottom: '0.5rem' }}>
            <label className="project-form-sublabel">Direct video URL:</label>
            <input
              type="url"
              value={formData.video_url}
              onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
              placeholder="https://example.com/video.mp4"
              className="project-form-field"
            />
          </div>
          <div>
            <label className="project-form-sublabel">Or upload video file:</label>
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              onChange={(e) => setFiles((f) => ({ ...f, video_file: e.target.files?.[0] || null }))}
              className="project-form-field"
            />
          </div>
          {videoPreview && (
            <div style={{ marginTop: '0.75rem', position: 'relative', display: 'inline-block' }}>
              <p className="project-form-preview-caption">Video preview:</p>
              <div style={{ maxWidth: '400px', aspectRatio: '16/9', background: '#000', borderRadius: '6px', overflow: 'hidden' }}>
                {videoPreview.type === 'embed' ? (
                  <iframe src={videoPreview.url} title="Video preview" style={{ width: '100%', height: '100%', border: 0 }} allowFullScreen />
                ) : (
                  <video src={videoPreview.url} controls style={{ width: '100%', height: '100%' }} />
                )}
              </div>
              {files.video_file && (
                <button
                  type="button"
                  onClick={() => {
                    setFiles((f) => ({ ...f, video_file: null }));
                    if (videoInputRef.current) videoInputRef.current.value = '';
                  }}
                  className="project-form-remove-btn"
                  title="Remove video file"
                  aria-label="Remove video file"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Project Date</label>
          <input
            type="date"
            value={formData.project_date}
            onChange={(e) => setFormData({ ...formData, project_date: e.target.value })}
            className="project-form-field"
          />
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label>Additional Photos & Videos (for project gallery)</label>
          <div style={{ marginBottom: '0.5rem' }}>
            <label className="project-form-sublabel">Upload files (images & videos):</label>
            <input
              ref={mediaInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={(e) => {
                const newFiles = Array.from(e.target.files || []);
                setFiles((f) => ({ ...f, media_files: [...f.media_files, ...newFiles] }));
                e.target.value = '';
              }}
              className="project-form-field"
            />
          </div>
          <div>
            <label className="project-form-sublabel">Or paste image/video URLs (one per line):</label>
            <textarea
              value={mediaUrls}
              onChange={(e) => setMediaUrls(e.target.value)}
              placeholder="https://example.com/image1.jpg&#10;https://example.com/video.mp4"
              rows="3"
              className="project-form-field"
              style={{ fontSize: '0.9rem' }}
            />
          </div>
          <small className="project-form-hint">
            Optional. Add extra images or videos to display on the project detail page.
          </small>
          {files.media_files.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.5rem', marginTop: '0.5rem' }}>
              {files.media_files.map((file, i) => (
                <div key={`media-${i}-${file.name}-${file.lastModified}`} className="project-form-media-item">
                  {file.type.startsWith('image/') ? (
                    <img src={URL.createObjectURL(file)} alt="" style={{ width: '100%', height: '80px', objectFit: 'cover' }} />
                  ) : (
                    <div className="project-form-media-placeholder">
                      <span style={{ fontSize: '1.5rem' }}>🎥</span>
                    </div>
                  )}
                  <small style={{ display: 'block', padding: '0.25rem', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{file.name}</small>
                  <button
                    type="button"
                    onClick={() => setFiles((f) => ({ ...f, media_files: f.media_files.filter((_, idx) => idx !== i) }))}
                    className="project-form-remove-btn project-form-remove-btn-small"
                    title="Remove file"
                    aria-label="Remove file"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {isEdit && existingMedia.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <label>Existing Gallery Media</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
              {existingMedia.map((media) => {
                const isImage = media.file_type === 'image' || /\.(jpg|jpeg|png|gif|webp)$/i.test(media.file_path || '');
                return (
                  <div key={media.id} className="project-form-existing-tile">
                    {isImage ? (
                      <img
                        src={getImageUrl(media.file_path)}
                        alt="Gallery"
                        style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'cover', marginBottom: '0.5rem' }}
                        onError={(e) => { e.target.onerror = null; e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="80" viewBox="0 0 100 80"><rect fill="#eee" width="100" height="80"/><text fill="#999" x="50" y="45" text-anchor="middle" font-size="12">No preview</text></svg>'; }}
                      />
                    ) : (
                      <div style={{ padding: '1rem', marginBottom: '0.5rem' }} className="project-form-media-placeholder">
                        <span>🎥 Video</span>
                      </div>
                    )}
                    <small style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{(media.file_path || '').split('/').pop()}</small>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="project-form-checkbox-row" style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <label>
            <input type="checkbox" checked={formData.is_featured} onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} />
            <span>Featured</span>
          </label>
          <label>
            <input type="checkbox" checked={formData.is_published} onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })} />
            <span>Published (visible on website)</span>
          </label>
          <label>
            <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
            <span>Active (enabled in system)</span>
          </label>
        </div>

        {error && <div className="project-form-error">{error}</div>}

        <div className="project-form-actions">
          <button
            type="submit"
            disabled={loading}
            style={{ padding: '0.75rem 1.5rem', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
          >
            {loading ? 'Saving...' : 'Save Project'}
          </button>
          <Link to="/projects" className="project-form-cancel">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
